import React from "react";
import { Chip } from "@mui/material";
import { cast, flow, getEnv, Instance, types } from "mobx-state-tree";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { endpoints } from "../api/ApiEndpoints";
import { Column, ColumnType, DisplayValues } from "../../components/datatable/interfaces";
import dateService from "../date-service/DateService";
import { translationService } from "../translation/TranslationService";
import { ActivityTypes, getActivityLabel, getAppActivity } from "./ActivityPricesStorage";
import { reaction } from "mobx";
import { organizationSubscriptionService } from "../organization/OrganizationSubscription";
import { authentication } from "../authentication/Authentication";
import { api } from "../api/ApiService";
import { currentUserService } from "../user/CurrentUserStorage";

interface ShopActivityItems {
    items: ShopActivity[],
    totalCount: number
}

// Must match MediaFileProvider.RestoredNamePrefix on the backend.
export const RESTORED_PREFIX = "[Restored] ";

// Mirrors the API's "FileExpiration" setting (appsettings.json): results — and the links
// that point at them — are removed this many days after the run. Only a fallback for rows
// that arrive without the server-computed expiration; the server's value always wins.
export const FILE_EXPIRATION_DAYS = 7;

// Activity timestamps come off the wire as zone-less UTC strings and can be empty on in-flight
// rows. dateService owns the "no zone means UTC" rule — parsing with `new Date()` here would
// read them as local time and shift every date derived from them by the viewer's offset.
function parseActivityDate(raw?: string): Date | undefined {
    if (!raw) return undefined;
    return dateService.parseApiDateTime(raw);
}

// The date a file's results — and with them its shareable link — stop working, derived from
// when the run was created. The fallback for every row that has no server-computed expiration,
// and the reason the countdown shrinks as the run ages instead of restarting at 7 days.
export function expirationFromCreated(created?: string | Date): Date | undefined {
    const start = created instanceof Date ? created : parseActivityDate(created);
    if (!start) return undefined;
    const expires = new Date(start);
    expires.setDate(expires.getDate() + FILE_EXPIRATION_DAYS);
    return expires;
}

export enum FileStates {
    Success,
    Failed,
    Running,
    Deleted,
    None
}

export interface ShopActivity {
    fileId: number,
    guid: string,
    filename: string,
    services: ActivityTypes[],
    timestamp: string,
    expiration: string,
    status: FileStates;
    contentType?: string;
}

const emptyShopActivity: ShopActivity = {
    filename: "",
    services: [ActivityTypes.VideoConversion],
    timestamp: "",
    fileId: 0,
    guid: "",
    expiration: "",
    status: FileStates.Running,
    contentType: ""
}

// Status "category" for the App Usage filter toggles. Precedence: deleted/running keep
// that state; a file past its expiration date is "expired"; a finished failure is
// "failed"; anything that succeeded and is still available is "valid".
export type ActivityCategory = "valid" | "failed" | "expired" | "running" | "deleted";

export function getActivityCategory(item: ShopActivity): ActivityCategory {
    if (item.status === FileStates.Deleted) return "deleted";
    if (item.status === FileStates.Running) return "running";
    if (item.expiration && new Date(item.expiration) < new Date()) return "expired";
    if (item.status === FileStates.Failed) return "failed";
    return "valid";
}

export const activityCategoryOptions: { value: ActivityCategory; label: string }[] = [
    { value: "valid",   label: "shop.shop-history.valid" },
    { value: "failed",  label: "shop.shop-history.failed" },
    { value: "expired", label: "shop.shop-history.expired" },
    { value: "running", label: "shop.shop-history.running" },
    { value: "deleted", label: "shop.shop-history.deleted" },
];

// A backup archive (.zip) uploaded for restore — a placeholder, not a real processing
// run. The App Usage list excludes these (other lists exclude them via allowedServiceSets).
export function isBackupFile(item: ShopActivity): boolean {
    const ct = (item.contentType ?? "").toLowerCase();
    const name = (item.filename ?? "").toLowerCase();
    return ct.includes("zip") || name.endsWith(".zip");
}

const appActivityOptions: ActivityTypes[] = [
    ActivityTypes.VideoConversion,
    ActivityTypes.VideoAnalysis,
    ActivityTypes.HighlightDetection,
    ActivityTypes.VideoOcr,
    ActivityTypes.VideoAnalysisAndSegmentation,
    ActivityTypes.VideoOcrAndAnalysis,
    ActivityTypes.VideoOcrAndSegmentation,
    // The three-way. It must be listed: getAppActivity() answers VideoOcrAnalysisAndSegmentation
    // for those files now, so without an option of its own a three-way run would match no
    // filter value at all and drop out of the list whenever the App column is filtered.
    ActivityTypes.VideoOcrAnalysisAndSegmentation,
];

const activitySingleSelectOptions = appActivityOptions.map(a => {
    const label = a === ActivityTypes.VideoAnalysisAndSegmentation
        ? "general.analysis-and-segmentation"
        : a === ActivityTypes.VideoOcrAndAnalysis
        ? "general.analysis-and-inspection-insights"
        : a === ActivityTypes.VideoOcrAnalysisAndSegmentation
        ? "general.analysis-inspection-insights-and-segmentation"
        : a === ActivityTypes.VideoOcrAndSegmentation
        // OCR + Segmentation is branded as Inspection Insights (OCR is the anchor app).
        ? "general.inspection-insights"
        : getActivityLabel([a] as ActivityTypes[]);
    return { value: a.toString(), label };
});

const statusValueOptions = [
    { value: FileStates.Success.toString(), label: "shop.shop-history.success" },
    { value: FileStates.Failed.toString(),  label: "shop.shop-history.failed" },
    { value: FileStates.Running.toString(), label: "shop.shop-history.running" },
    { value: FileStates.Deleted.toString(), label: "shop.shop-history.deleted" },
];

const columns : Column[] = [
    {
        key: "filename",
        name: "shop.shop-history.filename",
        type: ColumnType.String,
        sortable: true,
        filterable: true
    },
    {
        key: "service",
        name: "shop.shop-history.app",
        type: ColumnType.SingleSelect,
        sortable: true,
        filterable: true,
        valueOptions: activitySingleSelectOptions
    },
    {
        key: "timestamp",
        name: "shop.shop-history.timestamp",
        type: ColumnType.DateTime,
        sortable: true,
        filterable: true
    },
    {
        key: "expiration",
        name: "shop.shop-history.expiration",
        type: ColumnType.DateTime,
        sortable: true,
        filterable: true
    },
    {
        key: "status",
        name: "shop.shop-history.status",
        type: ColumnType.SingleSelect,
        sortable: true,
        filterable: true,
        valueOptions: statusValueOptions
    },
    {
        key: "outputLink",
        name: "shop.shop-history.appLink",
        type: ColumnType.String,
        containsLink: true,
    },
]

const ShopHistoryModel = types.model({
    serviceFilter: types.optional(types.frozen<undefined | ActivityTypes[]>(), undefined),
    stateFilter: types.optional(types.frozen<undefined | FileStates>(), undefined),
    allowedServiceSets: types.optional(types.frozen<undefined | ActivityTypes[][]>(), undefined),
    history: types.optional(types.frozen<ShopActivity[]>(), [emptyShopActivity]),
    clickPath: types.optional(types.frozen<string>(), ""),
    // App Usage status-toggle selection. Empty = show all categories.
    categoryFilter: types.optional(types.frozen<ActivityCategory[]>(), []),
    // App Usage "clean" list: when true the grid excludes backup .zip placeholders AND
    // files with no activity at all (empty service set — i.e. not in activity_logs).
    excludeBackups: types.optional(types.boolean, false)
})

export const ShopHistoryStorageModel = types
    .compose(ShopHistoryModel, DataGridStorageModel)
    .named("ShopHistoryStorageModel")
    .actions((self) => {
        return {
            deleteFiles: flow(function* (ids: number[]) {
                const endpoint = endpoints.get("mediafiles");
                yield getEnv(self).api.delete(endpoint, ids);
                self.deselectAll();
                yield self.load();
            }),
            mapResponse(response: ShopActivityItems) {
                const items = Array.isArray(response?.items) ? response.items : [];
                self.history = items;
                self.gridColumns = columns;
                if (self.clickPath !== "") {
                    self.gridColumns = self.gridColumns.filter(c => c.key !== "outputLink")
                }
                const base = self.excludeBackups ? items.filter(i => !isBackupFile(i) && (i.services?.length ?? 0) > 0) : items;
                const visible = self.categoryFilter.length === 0
                    ? base
                    : base.filter(i => self.categoryFilter.includes(getActivityCategory(i)));
                const mapped = visible.map(self.mapItem);
                self.data = self.clientSidePagination
                    ? mapped
                    : { totalCount: response?.totalCount ?? mapped.length, items: mapped };
            },
            // App Usage status toggles: filter the (already-loaded) rows by computed
            // category. Empty selection = show all. The grid's column filters apply on top.
            setCategoryFilter(categories: ActivityCategory[]) {
                self.categoryFilter = categories;
                const base = self.excludeBackups ? self.history.filter(i => !isBackupFile(i) && (i.services?.length ?? 0) > 0) : self.history;
                const visible = categories.length === 0
                    ? base
                    : base.filter(i => categories.includes(getActivityCategory(i)));
                const mapped = visible.map(self.mapItem);
                self.data = self.clientSidePagination
                    ? mapped
                    : { totalCount: visible.length, items: mapped };
            },
            mapItem(item: ShopActivity) {
                const services = Array.isArray(item?.services) ? item.services : [];
                const safeItem: ShopActivity = { ...item, services };
                const appActivity = getAppActivity(services) ?? ActivityTypes.VideoConversion;
                const status = safeItem.status ?? FileStates.None;
                const rawName = safeItem.filename ?? "";
                const isRestored = rawName.startsWith(RESTORED_PREFIX);
                const cleanName = isRestored ? rawName.slice(RESTORED_PREFIX.length) : rawName;
                var displayValues : DisplayValues = {
                    filename: {
                        value: cleanName,
                        toDisplay: cleanName,
                        child: isRestored
                            ? () => React.createElement(Chip, {
                                label: translationService.translate("shop.shop-history.restored"),
                                size: "small",
                                color: "info",
                                sx: { mr: 1, height: 18, fontSize: "0.65rem" }
                            })
                            : undefined
                    },
                    service: {value: appActivity, toDisplay: translationService.translate(getActivityLabel(services))},
                    timestamp: {value: safeItem.timestamp ?? "", toDisplay: safeItem.timestamp ? dateService.formatDateTime(safeItem.timestamp) : ""},
                    outputLink: {
                        value: safeItem.guid ?? "",
                        toDisplay: activityIsExpired(safeItem) ?
                            translationService.translate("shop.shop-history.expired") : translationService.translate("shop.shop-histroy.link"),
                        path: createAppLink(safeItem)
                    },
                    expiration: {value: safeItem.expiration ?? "", toDisplay: safeItem.expiration ? dateService.formatDateTime(safeItem.expiration) : ""},
                    status: {value: status, toDisplay: translationService.translate(mapStatus(status))},
                    guid: {value: safeItem.guid ?? "", toDisplay: safeItem.guid ?? ""}
                }
                return {
                    displayValues: displayValues,
                    id: safeItem.fileId
                }
            }
        }
    })
    .actions((self) => ({
        mockup() {
            self.history = [];
            self.clickPath = "";
        },
        wasActivityExecutedBy(sa: ShopActivity, services: ActivityTypes[]) {
            if (sa.services.length != services.length) return false;
            var activitySet = sa.services.sort();
            var servicesSet = services.sort();
            return activitySet.every((value, index) => value === servicesSet[index]);
        },
        getServiceProgressCount(services: ActivityTypes[]) {
            if (authentication.anonymous) return 0;
            return self.history.filter(sa => this.wasActivityExecutedBy(sa, services)).length;
        }
    }))
    .views((self) => ({
        get endpoint() {
            var endpoint = endpoints.get("shop_history");
            endpoint.queryParams = self.getDataQuery();
            if (self.serviceFilter !== undefined) {
                var query = self.serviceFilter.map(s => "&service=" + s.toString());
                endpoint.queryParams += query.join("");
            }
            if (self.stateFilter !== undefined) endpoint.queryParams += "&state=" + self.stateFilter;
            if (self.allowedServiceSets !== undefined && self.allowedServiceSets.length > 0) {
                endpoint.queryParams += self.allowedServiceSets.map(set => "&allowedServiceSets=" + set.join(",")).join("");
            }
            return endpoint;
        },
        get isLoading() {
            // See StorageModel.loadAttempted: the sentinel below stays in place when
            // canLoadItems is false, which would otherwise read as loading forever.
            if (self.error || self.loadAttempted) return false;
            return self.history[0] === emptyShopActivity;
        },
        get canLoadItems() {
            if (!authentication.anonymous) return true;
            // Anonymous users can only load if they have an organization-subscription
            // key in the env. Guard against the env's service being undefined during
            // a circular-import / init race (otherwise `load()` throws synchronously
            // before any 200 ever happens and the App.tsx splash hangs).
            const env: any = getEnv(self);
            return !!env?.organizationSubscriptionService?.key;
        },
        // When the given file's results (and therefore its shareable link) stop working.
        // Only files present in THIS store can be answered — the shared instance is filtered
        // to running files and to one page of rows, so callers need a fallback (see
        // expirationFromCreated) for a finished file that is not in the list.
        getExpirationForFile(fileId?: number): Date | undefined {
            if (fileId === undefined) return undefined;
            const activity = self.history.find((h) => h?.fileId === fileId);
            if (!activity) return undefined;
            const fromServer = parseActivityDate(activity.expiration);
            const created = parseActivityDate(activity.timestamp);
            // GetActivityHistory only fills Expiration on paginated responses; otherwise it
            // arrives as DateTime.MinValue, which would read as an expiry in the year 1. Trust
            // the server's value only when it actually falls after the run.
            if (fromServer && (!created || fromServer > created)) return fromServer;
            return expirationFromCreated(created);
        },
        get cannotDelete() {
            if (self.selectedItems.length < 1) return true;
            console.log(cast(self.selectedObjects))
            return self.selectedObjects
                .some(sa => activityIsExpired(
                    {...emptyShopActivity, 
                        expiration: sa.displayValues.expiration.value,
                        status: sa.displayValues.status.value
            }));
        }
    }));

export function mapStatus(status: FileStates) {
    switch (status) {
        case FileStates.Success: return "shop.shop-history.success";
        case FileStates.Failed: return "shop.shop-history.failed";
        case FileStates.Running: return "shop.shop-history.running";
        case FileStates.Deleted: return "shop.shop-history.deleted";
        default: return "";
    }
}

export function createAppLink(activity: ShopActivity) {
    if (activityIsExpired(activity)) return "";
    // Inspection Insights is the single one-page app for EVERY activity — OCR, Video Analysis,
    // Segmentation, Conversion, and any combination. All history links open there (it exposes
    // the Inspection / Video Analysis / Comparison tabs + the Mark-damage overlay and can run
    // any service), regardless of which service(s) the activity actually ran. This also avoids
    // linking to the legacy per-type routes, which don't exist when their feature flag is off.
    return "/inspection-insights?guid=" + activity.guid;
}

function activityIsExpired(activity: ShopActivity) {
    return new Date(activity.expiration) < new Date() || activity.status === FileStates.Deleted
}

export type ShopHistoryStorage = Instance<typeof ShopHistoryStorageModel>;

export const shopHistoryStorage = ShopHistoryStorageModel.create({stateFilter: FileStates.Running, autoLoad: false, clientSidePagination: true, useGridFiltering: true},{api, organizationSubscriptionService})

reaction(
    () => organizationSubscriptionService.key,
    async () => {
      if (authentication.anonymous) {
        shopHistoryStorage.mockup();
      } else {
        await shopHistoryStorage.load();
      }
    }
  );

reaction(
    () => authentication.anonymous,
    async () => {
      if (!authentication.anonymous && currentUserService.currentUser.id > -1) await shopHistoryStorage.load();
    }
  )
